import { useEffect, useMemo, useState } from "react";
import Seo from "./components/Seo";
import Header from "./components/Header";
import Hero from "./components/Hero";
import HotRank from "./components/HotRank";
import LiveFeed from "./components/LiveFeed";
import DeepReview from "./components/DeepReview";
import InfoSections from "./components/InfoSections";
import AboutSubscribe from "./components/AboutSubscribe";
import Footer from "./components/Footer";
import ArticleModal, { type ArticleLike } from "./components/ArticleModal";
import { feedItems, faqs, featuredDeep } from "@/mocks/chigua";

export default function Home() {
  const [category, setCategory] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [article, setArticle] = useState<ArticleLike | null>(null);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return feedItems.filter((it) => {
      const catOk = category === "all" || it.cat === category;
      const kwOk = !kw || it.title.toLowerCase().includes(kw) || it.excerpt.toLowerCase().includes(kw) || it.catLabel.toLowerCase().includes(kw);
      return catOk && kwOk;
    });
  }, [category, keyword]);

  const openFromFeed = (it: (typeof feedItems)[number]) => {
    setArticle({
      category: it.catLabel,
      title: it.title,
      meta: `${it.time} · ${it.views} 人围观 · ${it.comments} 条评论`,
      image: it.image,
      alt: it.alt,
      paragraphs: [
        it.excerpt,
        "本条为全网热议事件的现场梳理。看吃瓜正在持续跟进，相关进展与后续回应将在核实后第一时间更新，请以官方权威信息为准。",
      ],
    });
  };

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "看吃瓜-热门事件大瓜围观",
        url: "https://www.readdy.ai/",
        description: "看吃瓜带你围观全网热门事件，大瓜小瓜一网打尽，热门话题第一时间跟上节奏。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "大瓜现场",
        itemListElement: feedItems.map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: it.title,
          description: it.excerpt,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: featuredDeep.title,
        description: featuredDeep.dek,
        datePublished: featuredDeep.date,
        author: { "@type": "Organization", name: "看吃瓜编辑部" },
      },
    ],
    []
  );

  return (
    <>
      <Seo
        title="看吃瓜-热门事件大瓜围观 | 全网热议热点爆料一网打尽"
        description="看吃瓜带你围观全网热门事件，大瓜小瓜一网打尽。聚焦娱乐圈与热点爆料，把来龙去脉一次捋清，热门话题第一时间跟上节奏。"
        jsonLd={jsonLd}
      />

      <Header keyword={keyword} setKeyword={setKeyword} />

      <main>
        <Hero />
        <HotRank />
        <LiveFeed
          category={category}
          setCategory={setCategory}
          keyword={keyword}
          items={filtered}
          total={feedItems.length}
          onOpen={openFromFeed}
        />
        <DeepReview onRead={setArticle} />
        <InfoSections />
        <AboutSubscribe />
      </main>

      <Footer />

      {showTop && (
        <button
          type="button"
          aria-label="返回顶部"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-up-line text-lg" aria-hidden="true"></i>
        </button>
      )}

      <ArticleModal article={article} onClose={() => setArticle(null)} />
    </>
  );
}